import { PublicLayout } from '../../components/marketing/PublicLayout'
import { Link } from 'react-router-dom'
import { LogoIcon } from '@talktoast/shared'

export default function AboutPage() {
  return (
    <PublicLayout>
      <div className="py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <div className="mb-6 flex justify-center">
              <LogoIcon size="xl" alt="TalkToast" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-deep-charcoal mb-4">
              About TalkToast
            </h1>
            <p className="text-xl md:text-2xl text-gray-600">
              AI-powered meal planning that feels human
            </p>
          </div>

          {/* Mission Section */}
          <div className="prose prose-lg max-w-none mb-12">
            <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Our Mission</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              We believe meal planning should be exciting, not exhausting. TalkToast combines
              the power of AI with the warmth of conversation to help you discover meals you'll
              love, respect your dietary needs, and make grocery shopping effortless.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Whether you're cooking for one or feeding a family, following a special diet or
              exploring new cuisines, TalkToast is your friendly companion in the kitchen.
            </p>
          </div>

          {/* Values */}
          <div className="bg-gradient-to-br from-warm-cream to-butter-cream rounded-2xl p-8 md:p-12 mb-12 border border-soft-tan">
            <h2 className="text-3xl font-bold text-deep-charcoal mb-6">What We Believe</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold text-deep-charcoal mb-2 flex items-center gap-2">
                  <span role="img" aria-label="Heart">❤️</span>
                  Personalization
                </h3>
                <p className="text-base text-gray-600">
                  Every person's dietary needs and preferences are unique and deserve respect
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-deep-charcoal mb-2 flex items-center gap-2">
                  <span role="img" aria-label="Sparkles">✨</span>
                  Simplicity
                </h3>
                <p className="text-base text-gray-600">
                  Great tools should be intuitive, not overwhelming
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-deep-charcoal mb-2 flex items-center gap-2">
                  <span role="img" aria-label="Brain">🧠</span>
                  Intelligence
                </h3>
                <p className="text-base text-gray-600">
                  AI should enhance human creativity, not replace it
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-deep-charcoal mb-2 flex items-center gap-2">
                  <span role="img" aria-label="Globe">🌍</span>
                  Accessibility
                </h3>
                <p className="text-base text-gray-600">
                  Good food and meal planning should be available to everyone
                </p>
              </div>
            </div>
          </div>

          {/* How We're Different */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-deep-charcoal mb-6">How We're Different</h2>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-toast-gold rounded-full flex items-center justify-center text-white font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-deep-charcoal mb-2">
                    Conversational AI, Not Forms
                  </h3>
                  <p className="text-base text-gray-600">
                    Tired of endless dropdown menus? Just chat with our AI about what you want to eat.
                    It's natural, intuitive, and actually fun.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-toast-gold rounded-full flex items-center justify-center text-white font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-deep-charcoal mb-2">
                    Visual Calendar Planning
                  </h3>
                  <p className="text-base text-gray-600">
                    See your whole week at a glance. Drag meals around until they feel right.
                    Export to your favorite calendar app when you're done.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-toast-gold rounded-full flex items-center justify-center text-white font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-deep-charcoal mb-2">
                    Seamless Shopping Integration
                  </h3>
                  <p className="text-base text-gray-600">
                    One click sends your entire grocery list to Instacart. No manual copying,
                    no forgotten ingredients.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Beta Launch */}
          <div className="bg-gradient-to-br from-toast-gold/10 to-transparent rounded-2xl p-8 md:p-12 text-center border border-toast-gold/20">
            <h2 className="text-3xl font-bold text-deep-charcoal mb-4">
              Join Our Beta Launch
            </h2>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Our beta launches in June 2026 and we'd love for you to be part of our
              beta community. Help us shape the future of meal planning.
            </p>
            <Link
              to="/beta-signup"
              className="inline-block bg-toast-gold hover:bg-toast-gold-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Join Beta Waitlist
            </Link>
          </div>

          {/* Contact */}
          <div className="mt-12 text-center">
            <p className="text-base text-gray-600">
              Questions or feedback? We'd love to hear from you!
            </p>
            <a
              href="mailto:talktoastapp@gmail.com"
              className="text-toast-gold-700 hover:text-toast-gold-800 font-semibold text-lg transition-colors"
            >
              talktoastapp@gmail.com
            </a>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
